import { DateOnly } from '@if/core/date'
import {
  EPaymentFrequency,
  EPaymentPlan,
  EProductsInsurance,
  EProvince,
} from '@if/core/types'
import { getMaxPmtAmountWithAgentLeeway } from '@src/components/WorksheetForm/BaseWorksheetForm.helpers'
import { computeTotalCoverageFromFunding } from '@src/services/WorksheetComputer'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { ProductsComputeResultDto } from '@src/data/types/ProductsComputeResultDto'
import { ProductsProvider } from '@src/data/types/ProductsProvider'
import { ProductsInsurancePlan, ProductsWorksheet } from '@src/data/types/ProductsWorksheetSchema'
import { ProvinceConfigs } from '@src/data/types/ProvinceConfigs'
import { MechanicalWarrantyComputeResultDto } from '../EditWorksheet/EditMechanicalWarrantyWorksheetPage/types/ComputeMechanicalWarrantyResultDto'

export const canComputeFunding = (computeDto: FundingInfoDto, minLoanAmount: number): boolean => {
  return (
    computeDto.amountRequested >= minLoanAmount &&
    !!computeDto.paymentFrequency &&
    !!computeDto.paymentPlanId &&
    !!computeDto.deliveryOn &&
    !!computeDto.firstPaymentOn &&
    !!computeDto.term &&
    !!computeDto.interestRate &&
    !!computeDto.stateIso &&
    new DateOnly(computeDto.firstPaymentOn).getTime() >= computeDto.deliveryOn.getTime()
  )
}

export const getEmptyComputedFundingDto = (): FundingComputedDto => {
  return {
    lenderFee: 0,
    lenderFeeRate: 0,
    totalInterestAmount: 0,
    insuranceFee: 0,
    insuranceTax: 0,
    protectionPlanAmount: 0,
    paymentForFrequency: 0,
    effectiveRate: 0,
    requestId: '',
  }
}

export const getEmptyProductsComputeResponse = (): ProductsComputeResultDto => {
  return {
    paymentAmountForFrequency: 0,
    interestRate: 0,
    effectiveRate: 0,
    annualFees: 0,
  } as ProductsComputeResultDto
}

export const getEmptyMechanicalWarrantyComputeResponse = (): MechanicalWarrantyComputeResultDto => {
  return {
    amountRequested: 0,
    paymentAmount: 0,
  } as MechanicalWarrantyComputeResultDto
}

export const getHighCostRateForProvince = (provinceConfigs: ProvinceConfigs, stateIsoApplicant?: EProvince) => {
  if (stateIsoApplicant && provinceConfigs[stateIsoApplicant]?.highCostRate) {
    return provinceConfigs[stateIsoApplicant].highCostRate
  }
  return 0
}

export const getProvidersInsurancePlans = (
  productsWorksheet: ProductsWorksheet,
  productsProviders: ProductsProvider[],
): ProductsInsurancePlan[] => {
  const insurances: ProductsInsurancePlan[] = []
  if (productsWorksheet?.creditInsurance)
    insurances.push({
      ...productsWorksheet.creditInsurance,
      providerName: productsProviders.find((p) => p.id === productsWorksheet.creditInsurance.provider)?.name ?? '',
      type: EProductsInsurance.CreditInsurance,
    })
  if (productsWorksheet?.replacementOrGapInsurance)
    insurances.push({
      ...productsWorksheet.replacementOrGapInsurance,
      providerName:
        productsProviders.find((p) => p.id === productsWorksheet.replacementOrGapInsurance.provider)?.name ?? '',
      type: EProductsInsurance.ReplacementOrGapInsurance,
    })
  if (productsWorksheet?.extendedWarranty)
    insurances.push({
      ...productsWorksheet.extendedWarranty,
      providerName: productsProviders.find((p) => p.id === productsWorksheet.extendedWarranty.provider)?.name ?? '',
      type: EProductsInsurance.ExtendedWarranty,
    })
  return insurances
}
export const isPaymentAmountForFrequencyValid = (
  frequency: EPaymentFrequency,
  paymentAmountForFrequency: number,
  maxPaymentAmount: number,
) => {
  if (frequency === EPaymentFrequency.BiWeekly) {
    const convertedMonthlyPaymentAmount = Math.round(((paymentAmountForFrequency * 26) / 12) * 100) / 100
    return convertedMonthlyPaymentAmount <= maxPaymentAmount
  } else if (frequency === EPaymentFrequency.Monthly) {
    return paymentAmountForFrequency <= maxPaymentAmount
  }
  return false
}

export const getMaxPaymentAmountWithAgentLeeway = (paymentPlanId: EPaymentPlan, maxPaymentAmount: number): number =>
  getMaxPmtAmountWithAgentLeeway(paymentPlanId, maxPaymentAmount)

export interface TotalObligationComputedInfo {
  totalInterestAmount?: number
  lenderFee?: number
  insuranceFee?: number
  insuranceTax?: number
  protectionPlanAmount?: number
}

export const computeTotalObligation = (
  amountRequested: number,
  computedInfo?: TotalObligationComputedInfo,
): number =>
  amountRequested +
  (computedInfo?.totalInterestAmount ?? 0) +
  (computedInfo?.lenderFee ?? 0) +
  computeTotalCoverageFromFunding(computedInfo as FundingComputedDto | undefined)
